import { useCallback, useRef, useState } from 'react';
import type { ConfirmationModalConfig, ModalAction } from '../components/ConfirmationModal/ConfirmationModal';
import { ScaActionType } from '../models';
import type { DepartmentMetadata } from '../models';

// ── SCA modal types (maps to Angular's modal open/close key pairs) ──
type ScaModalType =
  | 'coreExtendedUncheck'       // Uncheck Core when Excitement is OFF
  | 'coreExtendedCheck'         // Check Core (move planned articles to core)
  | 'deleteExcitement'          // Uncheck Excitement
  | 'notificationExcitement';   // Uncheck Core when Excitement is ON

export interface ScaAction {
  scaActionType: ScaActionType;
  isCoreExtendedChecked: boolean;
  isExcitementChecked: boolean;
  includeExcitementInSummary: boolean;
}

interface UseScaModalOptions {
  /** Current department metadata (reactive – comes from hook state) */
  getMetadata: () => DepartmentMetadata;
  /** Setter for core-extended checkbox in parent state */
  setCoreExtendedChecked: (val: boolean) => void;
  /** Setter for excitement checkbox in parent state */
  setExcitementChecked: (val: boolean) => void;
  /** Setter for include-excitement-in-summary toggle in parent state */
  setIncludeExcitementToggle: (val: boolean) => void;
  /** Callback invoked when a SCA action should be dispatched to the API */
  onScaAction: (action: ScaAction) => void;
}

/**
 * Hook managing SCA (Selection-bar Checkbox Action) modal flows.
 * Mirrors the Angular ScaManagementService + selection-bar modal logic.
 *
 * Returns handlers for checkbox clicks and a modal config for rendering.
 */
export function useScaModal({
  getMetadata,
  setCoreExtendedChecked,
  setExcitementChecked,
  setIncludeExcitementToggle,
  onScaAction,
}: UseScaModalOptions) {
  const [modalType, setModalType] = useState<ScaModalType | null>(null);

  // Track whether the SCA selection was triggered by the user (prevents re-entrant API calls)
  const isScaSelectionByUser = useRef(false);

  // ── Close modal helper ────────────────────────────────────────────
  const closeModal = useCallback(() => {
    setModalType(null);
  }, []);

  // ── Core/Extended checkbox click handler ──────────────────────────
  // Mirrors Angular ScaManagementService.handleCoreCheckboxEvent
  const handleCoreCheckboxClick = useCallback(
    (checked: boolean) => {
      const metadata = getMetadata();

      if (checked) {
        // User is CHECKING Core/Extended → ask to move planned articles to core
        setCoreExtendedChecked(true);
        setModalType('coreExtendedCheck');
      } else if (metadata.isExcitementChecked) {
        // User is UN-checking Core/Extended
        // Cannot deselect core when excitement is active → acknowledge the uncheck
        // so React detects a state change, then modal OK/close will revert to true
        setCoreExtendedChecked(false);
        setModalType('notificationExcitement');
      } else {
        // Excitement is OFF → ask to move to planned articles
        setCoreExtendedChecked(false);
        setModalType('coreExtendedUncheck');
      }

      isScaSelectionByUser.current = true;
    },
    [getMetadata, setCoreExtendedChecked],
  );

  // ── Excitement checkbox click handler ─────────────────────────────
  // Mirrors Angular ScaManagementService.handleExcitementCheckboxEvent
  const handleExcitementCheckboxClick = useCallback(
    (checked: boolean) => {
      const metadata = getMetadata();

      if (checked) {
        // User is CHECKING Excitement
        if (metadata.isCoreExtendedChecked) {
          // Core already on → dispatch directly (ExcitementChecked), no modal
          setExcitementChecked(true);
          onScaAction({
            scaActionType: ScaActionType.ExcitementChecked,
            isCoreExtendedChecked: true,
            isExcitementChecked: true,
            includeExcitementInSummary: getMetadata().includeExcitementInSummary,
          });
        } else {
          // Core is OFF → auto-check core and open "move planned articles to core" modal
          setCoreExtendedChecked(true);
          setExcitementChecked(true);
          setModalType('coreExtendedCheck');
        }
      } else {
        // User is UN-checking Excitement → ask to delete excitement articles
        setExcitementChecked(false);
        setModalType('deleteExcitement');
      }

      isScaSelectionByUser.current = true;
    },
    [getMetadata, setCoreExtendedChecked, setExcitementChecked, onScaAction],
  );

  // ── Build modal config based on current modalType ─────────────────
  const getModalConfig = useCallback((): ConfirmationModalConfig => {
    const isOpen = modalType !== null;
    const noop = () => closeModal();

    if (!isOpen) {
      return { isOpen: false, header: '', message: '', actions: [], onClose: noop };
    }

    const dispatchAndClose = (
      scaActionType: ScaActionType,
      core: boolean,
      excitement: boolean,
      includeExcitement?: boolean,
    ) => {
      const includeExcitementInSummary = includeExcitement ?? getMetadata().includeExcitementInSummary;
      setCoreExtendedChecked(core);
      setExcitementChecked(excitement);
      onScaAction({ scaActionType, isCoreExtendedChecked: core, isExcitementChecked: excitement, includeExcitementInSummary });
      closeModal();
    };

    switch (modalType) {
      // ── Modal 1: Uncheck Core (Excitement OFF) ──────────────────
      // "Replace all core + extended articles with planned articles?"
      case 'coreExtendedUncheck': {
        const actions: ModalAction[] = [
          {
            label: 'YES',
            mode: 'primary',
            onClick: () => dispatchAndClose(
              ScaActionType.MoveToPlannedArticle,
              false,
              false,
            ),
          },
          {
            label: 'NO, delete the articles',
            mode: 'error',
            onClick: () => dispatchAndClose(
              ScaActionType.DeleteAll,
              false,
              false,
            ),
          },
        ];
        return {
          isOpen: true,
          header: 'Core & Extended Selection',
          message: 'Replace all core + extended articles with planned articles?',
          actions,
          onClose: () => {
            // Revert: re-check core
            setCoreExtendedChecked(true);
            closeModal();
          },
        };
      }

      // ── Modal 2: Check Core / Check Excitement when Core OFF ────
      // "Move planned articles to core?"
      case 'coreExtendedCheck': {
        const actions: ModalAction[] = [
          {
            label: 'YES',
            mode: 'primary',
            onClick: () => dispatchAndClose(
              ScaActionType.MoveToCore,
              true,
              getMetadata().isExcitementChecked,
            ),
          },
          {
            label: 'NO, delete the articles',
            mode: 'error',
            onClick: () => dispatchAndClose(
              ScaActionType.DeletePlannedArticle,
              true,
              getMetadata().isExcitementChecked,
            ),
          },
        ];
        return {
          isOpen: true,
          header: 'Core & Extended Selection',
          message: 'Move planned articles to core?',
          actions,
          onClose: () => {
            // Revert: uncheck core and excitement
            setCoreExtendedChecked(false);
            setExcitementChecked(false);
            closeModal();
          },
        };
      }

      // ── Modal 3: Uncheck Excitement ─────────────────────────────
      // "Delete all excitement articles?"
      case 'deleteExcitement': {
        const actions: ModalAction[] = [
          {
            label: 'YES',
            mode: 'primary',
            onClick: () => {
              setIncludeExcitementToggle(false);
              dispatchAndClose(
                ScaActionType.DeleteExcitement,
                getMetadata().isCoreExtendedChecked,
                false,
                false,
              );
            },
          },
          {
            label: 'NO, keep the articles',
            mode: 'information',
            onClick: () => {
              // Revert: re-check excitement
              setExcitementChecked(true);
              closeModal();
            },
          },
        ];
        return {
          isOpen: true,
          header: 'Excitement Selection',
          message: 'Delete all excitement articles?',
          actions,
          onClose: () => {
            // Revert: re-check excitement
            setExcitementChecked(true);
            closeModal();
          },
        };
      }

      // ── Modal 4: Notification (uncheck Core when Excitement ON) ─
      // "You cannot deselect core/extended when excitement is active"
      case 'notificationExcitement': {
        const actions: ModalAction[] = [
          {
            label: 'Ok',
            mode: 'warning',
            onClick: () => {
              // Revert: keep both checked
              setCoreExtendedChecked(true);
              setExcitementChecked(true);
              closeModal();
            },
          },
        ];
        return {
          isOpen: true,
          header: 'Notification',
          message: 'You cannot deselect core/extended when excitement is active',
          actions,
          onClose: () => {
            // Revert: keep both checked
            setCoreExtendedChecked(true);
            setExcitementChecked(true);
            closeModal();
          },
        };
      }

      default:
        return { isOpen: false, header: '', message: '', actions: [], onClose: noop };
    }
  }, [modalType, closeModal, getMetadata, setCoreExtendedChecked, setExcitementChecked, setIncludeExcitementToggle, onScaAction]);

  return {
    scaModalConfig: getModalConfig(),
    handleCoreCheckboxClick,
    handleExcitementCheckboxClick,
    isScaSelectionByUser,
  };
}
